import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import BuildProgressService from "../../services/BuildProgressService";

import "./build-progress.css";

const TICK_MILLISECONDS = 500;
const COUNTER_SHOWN_AFTER_SECONDS = 5;
const SECONDS_IN_HALF_MINUTE = 30;

class BuildProgress extends WComponent {
	constructor(props) {
		super(props);
		const millisecondsSinceStart = Date.now() - props.startedAt;
		this.state = { elapsedSeconds: Math.max(0, millisecondsSinceStart / 1000) };
		this.tickTimer = null;
	}

	componentDidMount() {
		this.tickTimer = setInterval(() => {
			const millisecondsSinceStart = Date.now() - this.props.startedAt;
			const elapsedSeconds = Math.max(0, millisecondsSinceStart / 1000);
			this.update({ elapsedSeconds });
		}, TICK_MILLISECONDS);
	}

	componentWillUnmount() {
		if (this.tickTimer) clearInterval(this.tickTimer);
	}

	render() {
		const { steps = [], slowAfterSeconds, longestSeconds = null } = this.props;
		const { elapsedSeconds } = this.state;

		const progress = BuildProgressService.progressAt({ steps, elapsedSeconds, slowAfterSeconds });
		const wholeElapsedSeconds = Math.floor(elapsedSeconds);
		const isCounterShown = elapsedSeconds >= COUNTER_SHOWN_AFTER_SECONDS;

		const isLongestKnown = Number.isFinite(longestSeconds) && longestSeconds > 0;
		const longestHalfMinutes = isLongestKnown ? Math.ceil(longestSeconds / SECONDS_IN_HALF_MINUTE) : 0;
		const longestWholeMinutes = Math.floor(longestHalfMinutes / 2);
		const hasHalfMinute = longestHalfMinutes % 2 === 1;
		const manyMinutesWords = hasHalfMinute ? `${longestWholeMinutes} and a half minutes` : `${longestWholeMinutes} minutes`;
		const fewMinutesWordsByHalfMinutes = { 1: "half a minute", 2: "a minute", 3: "a minute and a half" };
		const longestWords = fewMinutesWordsByHalfMinutes[longestHalfMinutes] || manyMinutesWords;
		const longestSentence = isLongestKnown ? ` Long answers can take up to ${longestWords}.` : "";
		const slowLine = `Taking a little longer than usual.${longestSentence}`;

		const currentStep = progress.steps.find(step => step.state === "current");
		const currentStepLabel = currentStep ? currentStep.label : "";
		const spokenSlowLine = progress.isSlow ? ` ${slowLine}` : "";
		const spokenProgress = `${currentStepLabel}${spokenSlowLine}`;

		return (
			<div className="build-progress">
				<ol className="build-progress__steps">
					{progress.steps.map(step => {
						const isDone = step.state === "done";
						const isCurrent = step.state === "current";
						return (
							<li key={step.key} className={clsx("build-progress__step", `build-progress__step--${step.state}`)}>
								<span className="build-progress__marker" aria-hidden="true">
									{isDone && <Icon.Check size={12} strokeWidth={2.5} />}
									{isCurrent && <span className="build-progress__dot" />}
								</span>
								<span className="build-progress__label" title={step.label}>
									{step.label}
								</span>
							</li>
						);
					})}
				</ol>
				{isCounterShown && (
					<div className="build-progress__counter" aria-hidden="true">
						{wholeElapsedSeconds} s
					</div>
				)}
				{progress.isSlow && (
					<div className="build-progress__slow" aria-hidden="true">
						{slowLine}
					</div>
				)}
				<div className="build-progress__spoken" role="status" aria-live="polite">
					{spokenProgress}
				</div>
			</div>
		);
	}
}

export default BuildProgress;
